// frontend/src/context/PlaidContext.js
import React, { createContext, useState, useContext, useCallback, useRef } from 'react';
import { AuthContext } from './AuthContext';
import api from '../services/api';
import {
  getRecurringStatus as computeRecurringStatus,
  isSameMerchant
} from '../utils/recurringMatch';

export const PlaidContext = createContext();

export const PlaidProvider = ({ children }) => {
  const { token } = useContext(AuthContext);
  const [accounts, setAccounts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [recurringExpenses, setRecurringExpenses] = useState([]);
  const [excludedMerchants, setExcludedMerchants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Track in-flight requests to prevent duplicates
  const inFlightRequestsRef = useRef(new Set());

  // Track whether data has been loaded during this session
  const dataLoadedRef = useRef({
    accounts: false,
    transactions: false,
    recurring: false
  });

  // Reset loaded state when token is cleared (logout or invalid token)
  // This ensures we'll fetch fresh data on next login
  React.useEffect(() => {
    if (!token) {
      dataLoadedRef.current = {
        accounts: false,
        transactions: false,
        recurring: false
      };
      inFlightRequestsRef.current.clear();
      setAccounts([]);
      setTransactions([]);
      setRecurringExpenses([]);
      setExcludedMerchants([]);
    }
  }, [token]);

  // Wrap the functions in useCallback to make them stable between renders
  const fetchAccounts = useCallback(async () => {
    // Skip if no token
    if (!token) return [];
    
    // Skip if already fetched in this session
    if (dataLoadedRef.current.accounts) return [];
    
    // Skip if request is already in flight
    if (inFlightRequestsRef.current.has('fetch-accounts')) return [];
    
    // Mark request as in flight
    inFlightRequestsRef.current.add('fetch-accounts');
    
    try {
      setLoading(true);
      
      // console.log('PlaidContext: Fetching accounts...');
      const response = await api.get('/api/plaid/accounts', {
        headers: { 'x-auth-token': token }
      });
      
      setAccounts(response.data);
      setError(null);
      
      // Mark data as loaded
      dataLoadedRef.current.accounts = true;
      
      return response.data;
    } catch (err) {
      // console.error('PlaidContext: Error fetching accounts:', err);
      setError(err.response?.data?.message || 'Failed to fetch accounts');
      return [];
    } finally {
      setLoading(false);
      
      // Remove from in-flight requests
      inFlightRequestsRef.current.delete('fetch-accounts');
    }
  }, [token]);

  const fetchTransactions = useCallback(async (startDate, endDate) => {
    // Skip if no token
    if (!token) return [];

    // Skip if we already fetched transactions this session (even if empty)
    // This prevents infinite loops for new users with no transactions
    if (!startDate && !endDate && dataLoadedRef.current.transactions) {
      // console.log('PlaidContext: Transactions already loaded, skipping fetch');
      return transactions; // Return cached transactions (may be empty for new users)
    }
    
    // Create request key with date params if provided
    const requestKey = startDate && endDate ? 
      `fetch-transactions-${startDate}-${endDate}` : 
      'fetch-transactions';
    
    // Skip if request is already in flight - wait for it instead of returning empty
    if (inFlightRequestsRef.current.has(requestKey)) {
      console.log(`PlaidContext: Request ${requestKey} already in flight, skipping...`);
      return []; // Return empty array to avoid triggering re-renders
    }
    
    // Mark request as in flight
    inFlightRequestsRef.current.add(requestKey);
    
    try {
      setLoading(true);
      
      let url = '/api/plaid/transactions';
      const params = {};
      
      if (startDate && endDate) {
        params.startDate = startDate;
        params.endDate = endDate;
      }
      
      console.log(`PlaidContext: Fetching transactions with key: ${requestKey}`);
      const response = await api.get(url, {
        headers: { 'x-auth-token': token },
        params
      });
      
      setTransactions(response.data);
      setError(null);
      
      // Mark data as loaded
      dataLoadedRef.current.transactions = true;
      
      return response.data;
    } catch (err) {
      // console.error('PlaidContext: Error fetching transactions:', err);
      setError(err.response?.data?.message || 'Failed to fetch transactions');
      return [];
    } finally {
      setLoading(false);
      
      // Remove from in-flight requests
      inFlightRequestsRef.current.delete(requestKey);
    }
  }, [token]);

  const syncTransactions = useCallback(async () => {
    // Skip if no token
    if (!token) return [];
    
    // Skip if request is already in flight
    if (inFlightRequestsRef.current.has('sync-transactions')) return [];
    
    // Mark request as in flight
    inFlightRequestsRef.current.add('sync-transactions');
    
    try {
      setLoading(true);
      
      // console.log('PlaidContext: Syncing transactions...');
      await api.post('/api/plaid/sync_transactions', {}, {
        headers: { 'x-auth-token': token }
      });

      setError(null);

      // After sync, force refresh transactions and accounts (balances updated server-side)
      dataLoadedRef.current.transactions = false;
      dataLoadedRef.current.accounts = false;
      const [txns] = await Promise.all([
        fetchTransactions(),
        fetchAccounts()
      ]);
      return txns;
    } catch (err) {
      // console.error('PlaidContext: Error syncing transactions:', err);
      setError(err.response?.data?.message || 'Failed to sync transactions');
      return [];
    } finally {
      setLoading(false);
      
      // Remove from in-flight requests
      inFlightRequestsRef.current.delete('sync-transactions');
    }
  }, [token, fetchTransactions, fetchAccounts]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);
  
  // Method to reset loaded state (useful for testing or forced refresh)
  const resetLoadedState = useCallback(() => {
    dataLoadedRef.current = {
      accounts: false,
      transactions: false
    };
  }, []);

  // Method to update a single transaction in state (e.g., after category change)
  const updateTransaction = useCallback((transactionId, updatedFields) => {
    setTransactions(prevTransactions =>
      prevTransactions.map(transaction =>
        transaction.transactionId === transactionId
          ? { ...transaction, ...updatedFields }
          : transaction
      )
    );
  }, []);

  // Method to update multiple transactions by pattern (for income source confirmation)
  const updateTransactionsByPattern = useCallback((pattern, updatedFields) => {
    const upperPattern = pattern.toUpperCase();
    setTransactions(prevTransactions =>
      prevTransactions.map(transaction => {
        const name = (transaction.merchantName || transaction.name || '').toUpperCase();
        if (name.includes(upperPattern)) {
          return { ...transaction, ...updatedFields };
        }
        return transaction;
      })
    );
  }, []);

  // Fetch recurring expenses + excluded merchants. Used by the Transaction History
  // and Recent Transactions "Recurring" column. Cached for the session like other
  // PlaidContext data; force=true bypasses the cache.
  const fetchRecurringData = useCallback(async (force = false) => {
    if (!token) return null;
    if (!force && dataLoadedRef.current.recurring) {
      return { recurringExpenses, excludedMerchants };
    }
    if (inFlightRequestsRef.current.has('fetch-recurring')) return null;
    inFlightRequestsRef.current.add('fetch-recurring');
    try {
      const [recRes, exRes] = await Promise.all([
        api.get('/api/recurring-expenses', { headers: { 'x-auth-token': token } }),
        api.get('/api/recurring-expenses/excluded', { headers: { 'x-auth-token': token } })
      ]);
      const list = Array.isArray(recRes.data) ? recRes.data : [];
      const excluded = exRes.data?.merchants || [];
      setRecurringExpenses(list);
      setExcludedMerchants(excluded);
      dataLoadedRef.current.recurring = true;
      return { recurringExpenses: list, excludedMerchants: excluded };
    } catch (err) {
      // Non-critical for the rest of the app; column will fall back to 'none'.
      console.warn('PlaidContext: failed to fetch recurring data', err?.message);
      return null;
    } finally {
      inFlightRequestsRef.current.delete('fetch-recurring');
    }
  }, [token, recurringExpenses, excludedMerchants]);

  // Sync helper for the Recurring column. Returns 'detected' | 'manual' | 'excluded' | 'none'.
  const getRecurringStatus = useCallback((transaction) => {
    return computeRecurringStatus(transaction, recurringExpenses, excludedMerchants);
  }, [recurringExpenses, excludedMerchants]);

  // Toggle handler for merchant-level toggles (used by the Monthly Plan recurring table).
  // Only supports going from recurring -> not-recurring (the Monthly Plan only shows
  // entries that are currently recurring). For 'detected' source, it reuses the
  // exclusion override; for 'manual' source, it deletes the ManualRecurringFlag.
  const setRecurringStatusForMerchant = useCallback(async (entry, recurring) => {
    if (!token || !entry) return null;
    const prevRecurring = recurringExpenses;
    const prevExcluded = excludedMerchants;
    const merchantName = entry.name;
    const source = entry.source || 'detected';

    let nextRecurring = prevRecurring;
    let nextExcluded = prevExcluded;

    if (recurring === false) {
      // Optimistically drop the row from the recurring list
      nextRecurring = prevRecurring.filter(r => r.name !== merchantName);
      if (source === 'detected') {
        nextExcluded = [...prevExcluded, merchantName];
      }
    } else {
      // Re-enabling from Monthly Plan isn't a flow today; mirror the state machine for safety.
      if (source === 'detected') {
        nextExcluded = prevExcluded.filter(m => m !== merchantName);
      }
    }

    setRecurringExpenses(nextRecurring);
    setExcludedMerchants(nextExcluded);

    try {
      if (recurring === false) {
        if (source === 'manual') {
          await api.delete(
            `/api/recurring-expenses/manual/${encodeURIComponent(merchantName)}`,
            { headers: { 'x-auth-token': token } }
          );
        } else {
          await api.put(
            '/api/recurring-expenses/override',
            { merchantName, amount: 0 },
            { headers: { 'x-auth-token': token } }
          );
        }
      } else if (source === 'detected') {
        // Re-enable: clear any exclusion override
        await api.put(
          '/api/recurring-expenses/override',
          { merchantName, amount: null },
          { headers: { 'x-auth-token': token } }
        );
      }
      dataLoadedRef.current.recurring = false;
      return true;
    } catch (err) {
      setRecurringExpenses(prevRecurring);
      setExcludedMerchants(prevExcluded);
      console.error('Failed to set recurring status for merchant', err);
      throw err;
    }
  }, [token, recurringExpenses, excludedMerchants]);

  // Toggle handler with optimistic local update + server PUT, with revert on failure.
  const setRecurringStatusForTransaction = useCallback(async (transaction, recurring) => {
    if (!token || !transaction) return null;

    const prevRecurring = recurringExpenses;
    const prevExcluded = excludedMerchants;
    const merchantName = transaction.name || transaction.merchantName;
    const amountAnchor = Math.abs(transaction.amount ?? 0);

    // Optimistic update — mirror server-side state machine.
    const currentStatus = computeRecurringStatus(transaction, prevRecurring, prevExcluded);
    let nextRecurring = prevRecurring;
    let nextExcluded = prevExcluded;

    if (recurring) {
      if (currentStatus === 'excluded') {
        nextExcluded = prevExcluded.filter(m => !isSameMerchant(m, merchantName));
      } else if (currentStatus === 'none') {
        nextRecurring = [
          ...prevRecurring,
          {
            _id: `optimistic-${merchantName}-${Date.now()}`,
            name: merchantName,
            amount: amountAnchor,
            amountAnchor,
            tolerance: 0.10,
            frequency: 'Monthly',
            source: 'manual'
          }
        ];
      }
    } else {
      if (currentStatus === 'detected') {
        nextExcluded = [...prevExcluded, merchantName];
      } else if (currentStatus === 'manual') {
        nextRecurring = prevRecurring.filter(r => !isSameMerchant(r.name, merchantName));
      }
    }

    setRecurringExpenses(nextRecurring);
    setExcludedMerchants(nextExcluded);

    try {
      const res = await api.put(
        `/api/transactions/${transaction.transactionId}/recurring`,
        { recurring },
        { headers: { 'x-auth-token': token } }
      );
      // Force the next visit to refetch the truth from the server.
      dataLoadedRef.current.recurring = false;
      return res.data?.status || null;
    } catch (err) {
      // Revert on failure.
      setRecurringExpenses(prevRecurring);
      setExcludedMerchants(prevExcluded);
      console.error('Failed to set recurring status', err);
      throw err;
    }
  }, [token, recurringExpenses, excludedMerchants]);

  return (
    <PlaidContext.Provider
      value={{
        accounts,
        transactions,
        recurringExpenses,
        excludedMerchants,
        loading,
        error,
        fetchAccounts,
        fetchTransactions,
        fetchRecurringData,
        syncTransactions,
        clearError,
        resetLoadedState,
        updateTransaction,
        updateTransactionsByPattern,
        getRecurringStatus,
        setRecurringStatusForTransaction,
        setRecurringStatusForMerchant
      }}
    >
      {children}
    </PlaidContext.Provider>
  );
};

export default PlaidContext;